// @flow
import * as React from 'react';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import ApiForm from 'esm-web-pro/containers/forms/ApiForm';
import { Box, Text } from 'grommet';
import { FormattedMessage } from 'react-intl';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';

const PANEL_ID = 'create-person-from-field-panel';
const EXCLUDE_FIELDS = ['sip_call_id', 'phone_contact_detail'];

const useCreatablePersonField = (fieldName: string, valueKey: string = 'uuid', multi = false): [
    Object[], { current: null | React.Node }, null | React.Node
] => {
    const [createdEntity, setCreatedEntity] = React.useState(null);
    const [showCreator, setShowCreator] = React.useState(null);
    const formikRef = React.useRef();

    const handleCreateOption = React.useCallback((inputValue: string) => {
        setShowCreator(inputValue);
    }, []);

    const handleFormSave = React.useCallback((response) => {
        setCreatedEntity(response);
        if (formikRef.current) {
            const formikBag = formikRef.current.getFormikBag();
            if (multi) {
                const currentValue = formikBag.values[fieldName] || [];
                formikBag.setFieldValue(fieldName, [
                    ...currentValue,
                    `${response[valueKey]}`,
                ]);
            } else {
                formikBag.setFieldValue(fieldName, `${response[valueKey]}`);
            }
            formikBag.setFieldTouched(fieldName, true);
        }
        setShowCreator(null);
    }, [fieldName, valueKey]);

    const handleClose = React.useCallback(() => {
        setShowCreator(null);
    }, []);

    const formatOptionLabel = React.useCallback((data, meta) => {
        if (meta.context === 'value') {
            return data.label;
        }
        if (data.__isNew__) {
            return (
                <Text weight="bold">
                    {data.label}
                </Text>
            );
        }
        let createdOn = (
            <FormattedMessage id="now" defaultMessage="Now" />
        );
        if (data._original && data._original.created) {
            const createdOnDate = parseISO(data._original.created);
            createdOn = format(createdOnDate, 'dd.MM.yyyy HH:mm');
        }
        // TODO: Labels/Tags
        return (
            <>
                {data.label}
                <br />
                <Text size="small">
                    <FormattedMessage id="created-entity-dropdown" defaultMessage="Created" />
                    {': '}
                    {createdOn}
                </Text>
            </>
        );
    }, []);

    const extraSchema = React.useMemo(() => {
        const choices = {};

        if (createdEntity) {
            choices[createdEntity[valueKey]] = createdEntity.name;
        }

        return [{
            [`${fieldName}.ui.choices`]: choices,
            [`${fieldName}.ui.creatable`]: true,
            [`${fieldName}.ui.onCreateOption`]: handleCreateOption,
            [`${fieldName}.ui.formatOptionLabel`]: formatOptionLabel,
            [`${fieldName}.ui.additional_filters`]: {
                ordering: '-created',
            },
        }];
    }, [fieldName, valueKey, createdEntity, handleCreateOption, formatOptionLabel]);

    const creatorInitial = React.useMemo(() => ({
        name: showCreator,
    }), [showCreator]);

    const creator = showCreator ? (
        <SidePane
            openInitially
            id={PANEL_ID}
            title={(
                <FormattedMessage
                    id="entities-createperson"
                    defaultMessage="Create person"
                />
            )}
        >
            <Box justify="between" height="100%">
                <ApiForm
                    excludeFields={EXCLUDE_FIELDS}
                    resourceName="client.person"
                    onSuccess={handleFormSave}
                    height="100%"
                    initial={creatorInitial}
                    onClose={handleClose}
                />
            </Box>
        </SidePane>
    ) : null;

    return [extraSchema, formikRef, creator];
};

export default useCreatablePersonField;
